import React from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import PaymentProcessingStore from "../../stores/PaymentProcessingStore";
import { useCartStore } from "../../stores/CartStore";
import { observer } from "mobx-react-lite";
import { Button } from "@mui/material";

type Props = {
    loading: boolean;
    total: number;
    onStripeSubmit: () => Promise<string>;
    onStripeSuccess: () => void;
    onStripeError: (error: any) => void;
};

const StripePaymentForm = observer(
    ({
        loading,
        total,
        onStripeError,
        onStripeSubmit,
        onStripeSuccess,
    }: Props) => {
        const [complete, setComplete] = React.useState(false);
        const stripe = useStripe();
        const elements = useElements();

        const handleSubmit = async (e: any) => {
            e.preventDefault();

            try {
                const clientSecret = await onStripeSubmit();

                if (elements == null || stripe == null) {
                    onStripeError(
                        "An error occurred while processing payment.",
                    );
                    return;
                }

                const el = elements.getElement(CardElement);

                if (el === null) {
                    onStripeError(
                        "An error occurred while processing payment.",
                    );
                    return;
                }

                if (!clientSecret) {
                    onStripeError(
                        "An error occurred while processing payment. Please try again later.",
                    );
                    return;
                }

                const { error, paymentIntent } =
                    await stripe.confirmCardPayment(clientSecret, {
                        payment_method: {
                            card: el,
                        },
                    });

                if (error) {
                    onStripeError(
                        error.message ??
                            "An error occurred while processing transaction.",
                    );
                } else {
                    onStripeSuccess();
                }
            } catch (_) {
                onStripeError("An error occurred while processing payment.");
            }
        };

        return (
            <form onSubmit={handleSubmit}>
                <CardElement
                    onChange={(e) => {
                        setComplete(e.complete);
                    }}
                />
                <Button
                    type="submit"
                    disabled={!complete || loading}
                    fullWidth
                    sx={{ mt: 2 }}
                    variant="contained"
                >
                    Complete Purchase
                </Button>
            </form>
        );
    },
);

export default StripePaymentForm;
